import React from "react";

export interface PatientInfoSection {
    title: string;
    items: Array<{ label: string; value: string }>;
}

interface CindraPatientInfoPanelProps {
    sections: PatientInfoSection[];
    patientName?: string;
    showHeader?: boolean;
    onClose?: (e: React.MouseEvent) => void | Promise<void>;
    maxHeight?: string;  // e.g., "400px" or "50vh" - when set, content will scroll
}

export const CindraPatientInfoPanel: React.FC<CindraPatientInfoPanelProps> = ({
    maxHeight,
    patientName,
    sections,
    showHeader = true,
    // Callbacks
    onClose
}) => {
    return (
        <div className="bg-white rounded-lg shadow-xl border-2 border-gray-400 flex flex-col" style={maxHeight ? { maxHeight } : undefined}>
            {/* Header */}
            {showHeader && (
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-blue-50 flex-shrink-0 rounded-t-lg">
                    <h3 className="text-base font-semibold text-gray-900 truncate">
                        {patientName ? `Patient Overview: ${patientName}` : 'Patient Overview'}
                    </h3>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors ml-4 flex-shrink-0"
                            title="Close"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            )}

            {/* Content - Scrollable when maxHeight is set */}
            <div className={`${maxHeight ? 'overflow-y-auto overflow-x-hidden flex-1' : ''} rounded-b-lg`}>
                {/* Single table for all sections to align labels globally */}
                <table className="w-full">
                    <tbody>
                        {sections.map((section, sectionIndex) => (
                            <React.Fragment key={sectionIndex}>
                                {/* Section header row with gray background */}
                                {section.title && (
                                    <tr>
                                        <td colSpan={2} className="px-4 py-2 bg-gray-100">
                                            <h2 className="text-sm font-semibold text-gray-900">
                                                {section.title}
                                            </h2>
                                        </td>
                                    </tr>
                                )}

                                {/* Section content rows */}
                                {section.items.map((item, itemIndex) => {
                                    // Check if this is a nested list (has newlines OR starts with marker)
                                    const isNestedList = item.value.includes('\n') || item.value.startsWith('•LIST_ITEM•');

                                    let lines: string[];
                                    if (isNestedList) {
                                        // Remove the marker prefix if present and split by newlines
                                        const cleanedValue = item.value.replace(/^•LIST_ITEM•/, '');
                                        lines = cleanedValue.includes('\n')
                                            ? cleanedValue.split('\n')
                                            : [cleanedValue];
                                    } else {
                                        lines = [item.value];
                                    }

                                    return (
                                        <tr key={itemIndex} className="text-xs">
                                            <td className="text-right text-gray-600 font-medium pr-3 py-1.5 px-4 align-top whitespace-nowrap">
                                                {item.label}:
                                            </td>
                                            <td className="text-left text-gray-900 py-1.5 pr-4 align-top">
                                                {isNestedList ? (
                                                    <ul className="list-disc list-inside space-y-0.5">
                                                        {lines.map((line, lineIndex) => (
                                                            <li key={lineIndex}>{line}</li>
                                                        ))}
                                                    </ul>
                                                ) : (
                                                    item.value
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
